/* ============================================
   Pendo Resource Center – Cox Automotive
   Styled to match the Interstate "How can we
   help?" slideout (Figma node 1967:1127).
   Tokens: surface #ffffff, border/muted #b2b2b2,
   primary/50 #ebf6ff, interactive #005ba8,
   on-surface #000 / subdued #5a5a5a, Roboto Flex.
   Selectors are anchored on the real Pendo RC
   DOM ("HomeViewModule" layout) + !important,
   since Pendo applies high-specificity inline
   styles.

   MAINTENANCE NOTE: the ._pendo-* and
   .pendo-mock-flexbox-* class names below are
   Pendo-private internals, not a public API. A
   Pendo agent update can rename them and silently
   break this styling — re-verify against the live
   RC DOM after any agent upgrade.
   ============================================ */

/* --- Panel: full-height right slideout below the bridge bar --- */
#pendo-resource-center-container {
  position: fixed !important;
  top: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  left: auto !important;
  inset: 0 0 0 auto !important;
  z-index: 300000 !important;
  width: 460px !important;
  max-width: 100vw !important;
  height: 100vh !important;
  max-height: 100vh !important;
  /* Own our scroll region: the panel is fixed at 100vh and our inner wrappers
     are height:auto, so a module list taller than the viewport would overflow
     and be unreachable unless Pendo happens to set overflow on a wrapper. Don't
     depend on that — scroll the panel itself. */
  overflow-y: auto !important;
  background: #ffffff !important;
  border-radius: 0 !important;
  /* Panel drop shadow (IDS shadow-lg-left). No modal scrim: Pendo doesn't
     render a backdrop element, and a CSS-only one (e.g. a box-shadow) can't
     block clicks to the page beneath, so we don't fake one. The RC is closed
     via its own close button. */
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.2) !important;
  font-family: "Roboto Flex", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  /* IDS enter: ids-base-duration-fast 250ms, ids-base-easing-enter-sharp */
  animation: pendo-rc-slide-in 0.25s cubic-bezier(0.32, 0.72, 0, 1) !important;
}

@keyframes pendo-rc-slide-in {
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
}

/* Inner Pendo wrappers should fill the panel and not force a fixed height */
#pendo-resource-center-container ._pendo-step-container-size,
#pendo-resource-center-container ._pendo-resource-center-view-container,
#pendo-resource-center-container ._pendo-resource-center-home-container {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
}

/* ============================================
   Header – white background, black left-aligned title
   (overrides Pendo's pink 7px top accent + centering)
   ============================================ */
#pendo-resource-center-container ._pendo-resource-center-home-title-container {
  background: #ffffff !important;
  border: 0 !important;
  text-align: left !important;
  padding: 0 !important;
}

#pendo-resource-center-container ._pendo-resource-center-home-title-text {
  color: #000000 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
  text-align: left !important;
  padding: 20px 24px !important;
  font-family: inherit !important;
}

/* Close "×" — neutral gray, top right */
#pendo-resource-center-container ._pendo-resource-center-close-button {
  color: #5a5a5a !important;
  font-size: 24px !important;
  right: 18px !important;
  top: 16px !important;
}

/* ============================================
   Home list – vertical stack of bordered cards
   ============================================ */
#pendo-resource-center-container ._pendo-resource-center-home-list {
  border: 0 !important;
  padding: 4px 16px 16px !important;
  height: auto !important;
}

/* Each module list item becomes a card */
#pendo-resource-center-container ._pendo-resource-center-module-list-item {
  margin: 12px 0 0 !important;
  padding: 14px 16px !important;
  border: 1px solid #b2b2b2 !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  cursor: pointer !important;
  transition: box-shadow 0.15s ease, border-color 0.15s ease !important;
}

/* Hover (Figma 2002:20695) – light-blue fill, gray border. Border width
   stays 1px (only the color changes) so the card never reflows. */
#pendo-resource-center-container ._pendo-resource-center-module-list-item:hover {
  background: #ebf6ff !important;
  border-color: #828282 !important;
}

/* Selected/pressed (Figma 2002:20695) – light-blue fill, 2px dark-blue
   border. Drawn as 1px border + 1px inset ring so the 2px look adds no
   layout shift. Placed after :hover so it wins while active/focused. */
#pendo-resource-center-container ._pendo-resource-center-module-list-item:active,
#pendo-resource-center-container ._pendo-resource-center-module-list-item:focus-within {
  background: #ebf6ff !important;
  border-color: #003468 !important;
  box-shadow: inset 0 0 0 1px #003468 !important;
}

/* Darken the icon tile (primary/50 → primary/100) so it stays distinct
   once the card fill turns light-blue (Figma 2002:20713). */
#pendo-resource-center-container ._pendo-resource-center-module-list-item:hover ._pendo-resource-center-module-list-item-style::before,
#pendo-resource-center-container ._pendo-resource-center-module-list-item:active ._pendo-resource-center-module-list-item-style::before,
#pendo-resource-center-container ._pendo-resource-center-module-list-item:focus-within ._pendo-resource-center-module-list-item-style::before {
  background-color: #cce8ff !important;
}

/* Never underline card text (the * covers all descendants in every state) */
#pendo-resource-center-container ._pendo-resource-center-module-list-item,
#pendo-resource-center-container ._pendo-resource-center-module-list-item * {
  text-decoration: none !important;
}

/* The link wrapper inside the card (display:inline by default) → block */
#pendo-resource-center-container ._pendo-resource-center-module-list-item > div {
  display: block !important;
  width: 100% !important;
}

/* ============================================
   Row layout – icon tile + text, left aligned.
   Pendo absolutely-positions its mock flexbox
   helpers; reset them so content flows in a row.
   ============================================ */
#pendo-resource-center-container ._pendo-resource-center-module-list-item-style {
  display: flex !important;
  align-items: center !important;
  gap: 14px !important;
}

/* Hide Pendo's notification bubble & right caret (not in the Figma design) */
#pendo-resource-center-container ._pendo-home-view-bubble,
#pendo-resource-center-container ._pendo-resource-center-right-caret {
  display: none !important;
}

/* Drop the empty flexbox cell that held the caret so text can fill the row */
#pendo-resource-center-container .pendo-mock-flexbox-element:has(._pendo-resource-center-right-caret) {
  display: none !important;
}

#pendo-resource-center-container .pendo-mock-flexbox-row {
  position: static !important;
  display: flex !important;
  align-items: center !important;
  width: auto !important;
  flex: 1 1 auto !important;
  min-height: 0 !important;
}
#pendo-resource-center-container .pendo-mock-flexbox-element {
  position: static !important;
  left: auto !important;
  top: auto !important;
  flex: 1 1 auto !important;
}

/* Icon tile: light-blue rounded square. The default icon is the "list-bullet"
   Heroicon (used by Release Notes and any module without a more specific
   aria-label match below). */
#pendo-resource-center-container ._pendo-resource-center-module-list-item-style::before {
  content: "" !important;
  flex: 0 0 auto !important;
  width: 40px !important;
  height: 40px !important;
  border-radius: 6px !important;
  background-color: #ebf6ff !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 22px 22px !important;
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M8.25%206.75h12M8.25%2012h12m-12%205.25h12M3.75%206.75h.007v.008H3.75V6.75Zm.375%200a.375.375%200%201%201-.75%200%20.375.375%200%200%201%20.75%200ZM3.75%2012h.007v.008H3.75V12Zm.375%200a.375.375%200%201%201-.75%200%20.375.375%200%200%201%20.75%200Zm-.375%205.25h.007v.008H3.75v-.008Zm.375%200a.375.375%200%201%201-.75%200%20.375.375%200%200%201%20.75%200Z%22%2F%3E%3C%2Fsvg%3E") !important;
}

/* ============================================
   Text – title (black, semibold) + subtitle (gray, regular)
   ============================================ */
#pendo-resource-center-container ._pendo-resource-center-module-list-item-text-container {
  padding: 0 !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item-title-text {
  color: #000000 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
  font-family: inherit !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item-subtitle-text {
  color: #5a5a5a !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 1.3 !important;
  text-transform: none !important;
  margin-top: 2px !important;
  font-family: inherit !important;
}

/* ============================================
   Per-card icons – Heroicons in #005ba8, matched on the
   module's aria-label. Pendo sets the aria-label on the
   card's title-text element (a descendant of the tile), not
   on the outer card, so we match with :has() on the card and
   paint the icon on the inner -style::before. Match the full
   title substring so titles never collide (e.g. "Support
   Chat", not just "Chat"). Modules with no match below (incl.
   "Release Notes") keep the default list-bullet icon set on
   the tile rule above.

   aria-label contains  → icon
   ----------------------------------------
   Announcements        → megaphone
   Support Chat         → chat-bubble-left-right
   How-To Guides        → document-text
   Help & Resources     → wrench
   Feedback             → chat-bubble-left
   Verification Code    → user
   (Release Notes / other → list-bullet, default)
   ============================================ */
#pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Announcements" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M10.34%2015.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5%204.5%200%200%201%200-9h.75c.704%200%201.402-.03%202.09-.09m0%209.18c.253.962.584%201.892.985%202.783.247.55.06%201.21-.463%201.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845%2020.845%200%200%201-1.44-4.282m3.102.069a18.03%2018.03%200%200%201-.59-4.59c0-1.586.205-3.124.59-4.59m0%209.18a23.848%2023.848%200%200%201%208.835%202.535M10.34%206.66a23.847%2023.847%200%200%200%208.835-2.535m0%200A23.74%2023.74%200%200%200%2018.795%203m.38%201.125a23.91%2023.91%200%200%201%201.014%205.395m-1.014%208.855c-.118.38-.245.754-.38%201.125m.38-1.125a23.91%2023.91%200%200%200%201.014-5.395m0-3.46c.495.413.811%201.035.811%201.73%200%20.695-.316%201.317-.811%201.73m0-3.46a24.347%2024.347%200%200%201%200%203.46%22%2F%3E%3C%2Fsvg%3E") !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Support Chat" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M20.25%208.511c.884.284%201.5%201.128%201.5%202.097v4.286c0%201.136-.847%202.1-1.98%202.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354%200-2.694-.055-4.02-.163a2.115%202.115%200%200%201-.825-.242m9.345-8.334a2.126%202.126%200%200%200-.476-.095%2048.64%2048.64%200%200%200-8.048%200c-1.131.094-1.976%201.057-1.976%202.192v4.286c0%20.837.46%201.58%201.155%201.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455%2048.455%200%200%200%2011.25%203c-2.115%200-4.198.137-6.24.402-1.608.209-2.76%201.614-2.76%203.235v6.226c0%201.621%201.152%203.026%202.76%203.235.577.075%201.157.14%201.74.194V21l4.155-4.155%22%2F%3E%3C%2Fsvg%3E") !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="How-To Guides" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M19.5%2014.25v-2.625a3.375%203.375%200%200%200-3.375-3.375h-1.5A1.125%201.125%200%200%201%2013.5%207.125v-1.5a3.375%203.375%200%200%200-3.375-3.375H8.25m0%2012.75h7.5m-7.5%203H12M10.5%202.25H5.625c-.621%200-1.125.504-1.125%201.125v17.25c0%20.621.504%201.125%201.125%201.125h12.75c.621%200%201.125-.504%201.125-1.125V11.25a9%209%200%200%200-9-9Z%22%2F%3E%3C%2Fsvg%3E") !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Help & Resources" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M11.42%2015.17%2017.25%2021A2.652%202.652%200%200%200%2021%2017.25l-5.877-5.877M11.42%2015.17l2.496-3.03c.317-.384.74-.626%201.208-.766M11.42%2015.17l-4.655%205.653a2.548%202.548%200%201%201-3.586-3.586l6.837-5.63m5.108-.233c.55-.164%201.163-.188%201.743-.14a4.5%204.5%200%200%200%204.486-6.336l-3.276%203.277a3.004%203.004%200%200%201-2.25-2.25l3.276-3.276a4.5%204.5%200%200%200-6.336%204.486c.091%201.076-.071%202.264-.904%202.95l-.102.085m-1.745%201.437L5.909%207.5H4.5L2.25%203.75l1.5-1.5L7.5%204.5v1.409l4.26%204.26m-1.745%201.437%201.745-1.437m6.615%208.206L15.75%2015.75M4.867%2019.125h.008v.008h-.008v-.008Z%22%2F%3E%3C%2Fsvg%3E") !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Feedback" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M7.5%208.25h9m-9%203H12m-9.75%201.51c0%201.6%201.123%202.994%202.707%203.227%201.129.166%202.27.293%203.423.379.35.026.67.21.865.501L12%2021l2.755-4.133a1.14%201.14%200%200%201%20.865-.501%2048.172%2048.172%200%200%200%203.423-.379c1.584-.233%202.707-1.626%202.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394%2048.394%200%200%200%2012%203c-2.392%200-4.744.175-7.043.513C3.373%203.746%202.25%205.14%202.25%206.741v6.018Z%22%2F%3E%3C%2Fsvg%3E") !important;
}
#pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Verification Code" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23005ba8%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M15.75%206a3.75%203.75%200%201%201-7.5%200%203.75%203.75%200%200%201%207.5%200ZM4.501%2020.118a7.5%207.5%200%200%201%2014.998%200A17.933%2017.933%200%200%201%2012%2021.75c-2.676%200-5.216-.584-7.499-1.632Z%22%2F%3E%3C%2Fsvg%3E") !important;
}

/* ════════════════════════════════════════════════════════════════════════
   DARK MODE (US2003211 AC7/AC8/AC9/AC10)

   Additive layer ONLY — every rule below is scoped under the document hook
   html[data-bridge-support-menu-theme="dark"], which App.ts reflects onto
   <html> from bb_theme="Interstate-Dark" while the Pendo CSS is injected
   (ref-counted via pendo-override.ts increment/decrementDarkThemeHook). The light rules above are
   untouched and remain the default cascade (AC10 additive-only); when the
   hook is absent NONE of this applies.

   TOKEN SOURCE: values below were pixel-sampled directly from the Figma frame
   "Get Help Pendo Menu — dark" (file x86BBXFi9M4n9TRWz79QYV, node 2053-158),
   not guessed:
     - panel / header / card surface  #17191f  (neutral dark charcoal — NOT navy)
     - icon tile                      #113455  (navy blue) + light icon glyphs
     - card border (subtle)           #21252e
     - title text                     #ffffff
     - subtitle text                  #9aa0a6  (muted neutral gray)
   These remain pending final confirmation against Jon Ferry's authoritative
   dark CSS (still the carried dependency) — but they now match the published
   design rather than a derivation. Contrast spot-check on #17191f: white
   title ≈ 14:1, #9aa0a6 subtitle ≈ 6.5:1, light icon on #113455 ≈ 6:1 — all
   clear AA. Confirm in the manual visual gate.
   ════════════════════════════════════════════════════════════════════════ */

/* Shared dark palette — SINGLE SOURCE OF TRUTH (PR #109 review: these hex values were
   duplicated as raw literals across this file and SupportMenuTrigger.css, which would
   drift). Defined on the document hook so the custom properties inherit everywhere,
   INCLUDING across the shadow boundary into the Get Help button's shadow root
   (Firefox-safe — unlike :host-context()). Reconciling against Jon Ferry's authoritative
   dark CSS is now a one-line-per-token swap here rather than a multi-rule edit.
   PROVISIONAL: pixel-sampled from Figma node 2053-158, pending final confirmation.
   NOTE: icon-glyph colour (#cfe2f5) cannot be tokenized — it is embedded inside the
   SVG data-URIs below, where var() does not resolve; it stays a literal there. */
html[data-bridge-support-menu-theme="dark"] {
  --sm-dark-surface: #17191f; /* panel / header / card surface */
  --sm-dark-surface-hover: #1f2229; /* card hover/active lift */
  --sm-dark-card-border: #21252e; /* subtle card border */
  --sm-dark-card-border-hover: #3c4049;
  --sm-dark-accent: #5aa9e6; /* panel selected/focus ring + border */
  --sm-dark-title: #ffffff;
  --sm-dark-subtitle: #9aa0a6;
  --sm-dark-close: #c8ccd2;
  --sm-dark-icon-tile: #113455; /* navy icon tile */
  --sm-dark-icon-tile-hover: #1a466e;
  /* NOTE: the Get Help button dark tokens (--sm-dark-btn-*) used to live here and
     inherit across the shadow boundary, but this stylesheet is only injected in Pendo
     mode — so the button stayed light in legacy mode. They now live in
     SupportMenuTrigger.css under :host([dark]) so the button themes in BOTH modes
     (dark-button fix, 2026-06-11). This hook + block stays scoped to the Pendo
     container, which only exists in Pendo mode. */
}

/* Panel: neutral dark charcoal surface (replaces light #ffffff). */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container {
  background: var(--sm-dark-surface) !important;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.6) !important;
}

/* Header — same charcoal, white left-aligned title */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-home-title-container {
  background: var(--sm-dark-surface) !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-home-title-text {
  color: var(--sm-dark-title) !important;
}

/* Close "×" — light gray on the charcoal header */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-close-button {
  color: var(--sm-dark-close) !important;
}

/* Cards — same charcoal surface, subtle border to separate from the panel */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item {
  background: var(--sm-dark-surface) !important;
  border-color: var(--sm-dark-card-border) !important;
}

/* Hover — lift the card a step, brighten the border */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:hover {
  background: var(--sm-dark-surface-hover) !important;
  border-color: var(--sm-dark-card-border-hover) !important;
}

/* Selected/pressed — bright-blue border + inset ring (visible on dark) */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:active,
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:focus-within {
  background: var(--sm-dark-surface-hover) !important;
  border-color: var(--sm-dark-accent) !important;
  box-shadow: inset 0 0 0 1px var(--sm-dark-accent) !important;
}

/* Title (white) + subtitle (muted neutral gray) */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item-title-text {
  color: var(--sm-dark-title) !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item-subtitle-text {
  color: var(--sm-dark-subtitle) !important;
}

/* Base icon tile — navy background (#113455 token) + the default list-bullet glyph
   recolored #005ba8 → #cfe2f5 for legibility on the navy tile (the light-mode dark
   glyphs would be ~1.2:1, invisible). PR #109 review: background-color and
   background-image were two separate rule blocks with the SAME selector (a cascade-
   ordering trap) — merged here into one. The per-icon background-image overrides below
   (keyed by the card's aria-label) and the :hover/:active/:focus-within tile colour are
   intentionally separate selectors. */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item-style::before {
  background-color: var(--sm-dark-icon-tile) !important;
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M8.25%206.75h12M8.25%2012h12m-12%205.25h12M3.75%206.75h.007v.008H3.75V6.75Zm.375%200a.375.375%200%201%201-.75%200%20.375.375%200%200%201%20.75%200ZM3.75%2012h.007v.008H3.75V12Zm.375%200a.375.375%200%201%201-.75%200%20.375.375%200%200%201%20.75%200Zm-.375%205.25h.007v.008H3.75v-.008Zm.375%200a.375.375%200%201%201-.75%200%20.375.375%200%200%201%20.75%200Z%22%2F%3E%3C%2Fsvg%3E") !important;
}

/* Icon tile hover/active/focus — one step brighter navy. */
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:hover ._pendo-resource-center-module-list-item-style::before,
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:active ._pendo-resource-center-module-list-item-style::before,
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:focus-within ._pendo-resource-center-module-list-item-style::before {
  background-color: var(--sm-dark-icon-tile-hover) !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Announcements" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M10.34%2015.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5%204.5%200%200%201%200-9h.75c.704%200%201.402-.03%202.09-.09m0%209.18c.253.962.584%201.892.985%202.783.247.55.06%201.21-.463%201.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845%2020.845%200%200%201-1.44-4.282m3.102.069a18.03%2018.03%200%200%201-.59-4.59c0-1.586.205-3.124.59-4.59m0%209.18a23.848%2023.848%200%200%201%208.835%202.535M10.34%206.66a23.847%2023.847%200%200%200%208.835-2.535m0%200A23.74%2023.74%200%200%200%2018.795%203m.38%201.125a23.91%2023.91%200%200%201%201.014%205.395m-1.014%208.855c-.118.38-.245.754-.38%201.125m.38-1.125a23.91%2023.91%200%200%200%201.014-5.395m0-3.46c.495.413.811%201.035.811%201.73%200%20.695-.316%201.317-.811%201.73m0-3.46a24.347%2024.347%200%200%201%200%203.46%22%2F%3E%3C%2Fsvg%3E") !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Support Chat" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M20.25%208.511c.884.284%201.5%201.128%201.5%202.097v4.286c0%201.136-.847%202.1-1.98%202.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354%200-2.694-.055-4.02-.163a2.115%202.115%200%200%201-.825-.242m9.345-8.334a2.126%202.126%200%200%200-.476-.095%2048.64%2048.64%200%200%200-8.048%200c-1.131.094-1.976%201.057-1.976%202.192v4.286c0%20.837.46%201.58%201.155%201.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455%2048.455%200%200%200%2011.25%203c-2.115%200-4.198.137-6.24.402-1.608.209-2.76%201.614-2.76%203.235v6.226c0%201.621%201.152%203.026%202.76%203.235.577.075%201.157.14%201.74.194V21l4.155-4.155%22%2F%3E%3C%2Fsvg%3E") !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="How-To Guides" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M19.5%2014.25v-2.625a3.375%203.375%200%200%200-3.375-3.375h-1.5A1.125%201.125%200%200%201%2013.5%207.125v-1.5a3.375%203.375%200%200%200-3.375-3.375H8.25m0%2012.75h7.5m-7.5%203H12M10.5%202.25H5.625c-.621%200-1.125.504-1.125%201.125v17.25c0%20.621.504%201.125%201.125%201.125h12.75c.621%200%201.125-.504%201.125-1.125V11.25a9%209%200%200%200-9-9Z%22%2F%3E%3C%2Fsvg%3E") !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Help & Resources" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M11.42%2015.17%2017.25%2021A2.652%202.652%200%200%200%2021%2017.25l-5.877-5.877M11.42%2015.17l2.496-3.03c.317-.384.74-.626%201.208-.766M11.42%2015.17l-4.655%205.653a2.548%202.548%200%201%201-3.586-3.586l6.837-5.63m5.108-.233c.55-.164%201.163-.188%201.743-.14a4.5%204.5%200%200%200%204.486-6.336l-3.276%203.277a3.004%203.004%200%200%201-2.25-2.25l3.276-3.276a4.5%204.5%200%200%200-6.336%204.486c.091%201.076-.071%202.264-.904%202.95l-.102.085m-1.745%201.437L5.909%207.5H4.5L2.25%203.75l1.5-1.5L7.5%204.5v1.409l4.26%204.26m-1.745%201.437%201.745-1.437m6.615%208.206L15.75%2015.75M4.867%2019.125h.008v.008h-.008v-.008Z%22%2F%3E%3C%2Fsvg%3E") !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Feedback" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M7.5%208.25h9m-9%203H12m-9.75%201.51c0%201.6%201.123%202.994%202.707%203.227%201.129.166%202.27.293%203.423.379.35.026.67.21.865.501L12%2021l2.755-4.133a1.14%201.14%200%200%201%20.865-.501%2048.172%2048.172%200%200%200%203.423-.379c1.584-.233%202.707-1.626%202.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394%2048.394%200%200%200%2012%203c-2.392%200-4.744.175-7.043.513C3.373%203.746%202.25%205.14%202.25%206.741v6.018Z%22%2F%3E%3C%2Fsvg%3E") !important;
}
html[data-bridge-support-menu-theme="dark"] #pendo-resource-center-container ._pendo-resource-center-module-list-item:has([aria-label*="Verification Code" i]) ._pendo-resource-center-module-list-item-style::before {
  background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2024%2024%22%20stroke-width%3D%221.5%22%20stroke%3D%22%23cfe2f5%22%3E%3Cpath%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20d%3D%22M15.75%206a3.75%203.75%200%201%201-7.5%200%203.75%203.75%200%200%201%207.5%200ZM4.501%2020.118a7.5%207.5%200%200%201%2014.998%200A17.933%2017.933%200%200%201%2012%2021.75c-2.676%200-5.216-.584-7.499-1.632Z%22%2F%3E%3C%2Fsvg%3E") !important;
}

